// =============================================
// WebSearch Verifier Service
// v3.0: Verify claims via LLM with web_search_preview tool
// =============================================

import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { logPublic } from "./contentLogger";
import { getOwnDomain, isSameDomain } from "./ownDomain";
import { checkUrlAlive } from "./serpResearch";
import type { ExtractedClaim } from "./claimExtractor";

// =============================================
// Types
// =============================================

export type VerificationStatus = "confirmed" | "modified" | "unverified";

export interface VerifiedClaim extends ExtractedClaim {
  status: VerificationStatus;
  sourceName: string | null;
  sourceYear: string | null;
  sourceUrl: string | null;
  correctedValue: string | null;
  verificationNote: string;
}

// =============================================
// Helpers
// =============================================

function cleanSourceUrl(url: string): string {
  return url
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .slice(0, 120);
}

/**
 * Detect poor-quality source URLs that LLM web_search may return — root domains
 * and shallow generic landing pages that don't actually host claim-supporting
 * content. Operates on already cleanSourceUrl-stripped strings (no scheme,
 * no www., max 120 chars).
 *
 * Returns null when URL is acceptable, otherwise the reason key for forensics.
 *
 * Pairs with prompt requirement that source_excerpt MUST be a verbatim quote
 * from the URL — empty excerpt also signals unreal verification.
 */
const POOR_URL_PATH_RE = /^[^/]+\/?$|^[^/]+\/(?:home|index|about|kontakt|contact|main|products?|services?|tools?|blog|news|press|media|info)\/?$/i;
function poorUrlReason(
  url: string | null,
  excerpt: string,
): "root_or_shallow" | "too_short" | "no_excerpt" | null {
  if (!url) return "root_or_shallow";
  if (url.length <= 25) return "too_short";
  if (POOR_URL_PATH_RE.test(url)) return "root_or_shallow";
  if (!excerpt) return "no_excerpt";
  return null;
}

function getSourceLabel(language: string): string {
  switch (language) {
    case "pl": return "Źródło";
    case "de": return "Quelle";
    default: return "Source";
  }
}

// =============================================
// Verification
// =============================================

const VERIFICATION_SCHEMA = {
  type: "object" as const,
  properties: {
    verifications: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          claim_id: { type: "string" as const },
          status: { type: "string" as const, enum: ["confirmed", "modified", "unverified"] },
          source_name: { type: "string" as const },
          source_year: { type: "string" as const },
          source_url: { type: "string" as const },
          source_excerpt: { type: "string" as const },
          corrected_value: { type: "string" as const },
          note: { type: "string" as const },
        },
        required: ["claim_id", "status", "source_name", "source_year", "source_url", "source_excerpt", "corrected_value", "note"] as const,
        additionalProperties: false as const,
      },
    },
  },
  required: ["verifications"] as const,
  additionalProperties: false as const,
};

/**
 * Verify claims using LLM with web search capability.
 * Generates verification questions, then searches and evaluates.
 */
export async function verifyClaims(
  claims: ExtractedClaim[],
  language: string,
  mapId?: string,
  nodeId?: string,
): Promise<{ verified: VerifiedClaim[]; costUsd: number }> {
  if (claims.length === 0) {
    return { verified: [], costUsd: 0 };
  }

  let totalCost = 0;

  // Load prompt from DB (user-editable in Settings → Prompty AI)
  const dbPrompt = await loadPrompt("enrichment");

  // Step 1: Generate verification questions (batch, cheap model)
  const claimsList = claims
    .map((c) => `${c.id}: "${c.text}" (kontekst: ${c.context.slice(0, 200)})`)
    .join("\n");

  const questionsTemplateVars: Record<string, string> = {
    language,
    claims_list: claimsList,
  };

  const questionsPrompt = dbPrompt
    ? fillTemplate(dbPrompt.template, questionsTemplateVars)
    : `Dla każdego twierdzenia (claim) wygeneruj pytanie weryfikacyjne w języku ${language}.

Claims:
${claimsList}

Zwróć JSON array: [{ "claim_id": "...", "question": "..." }]`;

  const claimsStageConfig = getStageConfig("enrichment_claims");
  const claimsProvider = getLLMProvider(dbPrompt?.provider || claimsStageConfig.provider);
  const questionsResponse = await claimsProvider.call(questionsPrompt, {
    model: dbPrompt?.modelId || claimsStageConfig.model,

    temperature: dbPrompt?.temperature ?? 0.3,
    step: "enrichment_claims", mapId, nodeId,
  });
  totalCost += questionsResponse.costUsd || 0;

  let questions: Array<{ claim_id: string; question: string }>;
  try {
    const parsed = parseLLMJSON<any>(questionsResponse.text);
    // LLM may return { questions: [...] } or [...] directly
    const arr = Array.isArray(parsed) ? parsed : (parsed?.questions || parsed?.results || []);
    questions = Array.isArray(arr) && arr.length > 0
      ? arr
      : claims.map((c) => ({ claim_id: c.id, question: c.text }));
  } catch {
    questions = claims.map((c) => ({ claim_id: c.id, question: c.text }));
  }

  // Step 2: Verify each claim with web search
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const timeWindow = currentMonth >= 7
    ? `Priorytet: dane z ${currentYear}. Fallback: ${currentYear - 1}.`
    : `Priorytet: dane z ${currentYear - 1} i Q1 ${currentYear}.`;

  const claimsForVerification = claims.map((c) => {
    const q = questions.find((q) => q.claim_id === c.id);
    return `- ${c.id}: Twierdzenie: "${c.text}" | Pytanie: "${q?.question || c.text}" | Kontekst: "${c.context.slice(0, 150)}"`;
  }).join("\n");

  // Load DB prompt (path-bearing URL + verbatim excerpt requirements). Fallback inline
  // mirrors the DB v1 content for defense-in-depth if loadPrompt fails.
  const verifyDbPrompt = await loadPrompt("enrichment_verify");
  const verifyTemplateVars: Record<string, string> = {
    claims: claimsForVerification,
    language,
    time_window: timeWindow,
  };
  const verifyPrompt = verifyDbPrompt
    ? fillTemplate(verifyDbPrompt.template, verifyTemplateVars)
    : `Jesteś fact-checkerem. Zweryfikuj poniższe twierdzenia, szukając wiarygodnych źródeł.

## Twierdzenia do weryfikacji
${claimsForVerification}

## Zasady
- ${timeWindow}
- Szukaj w języku: ${language}
- URL musi być KONKRETNĄ stroną artykułu (NIE root domain, NIE landing page)
- source_excerpt: verbatim cytat (1-2 zdania) z URL wspierający claim — wymagany dla confirmed/modified
- Status: "confirmed" / "modified" / "unverified"
- Jeśli brak źródła lub brak konkretnej strony: status="unverified", puste stringi

Zwróć JSON z verifications array.`;

  const verifyStageConfig = getStageConfig("enrichment_verify");
  const verifyProvider = getLLMProvider(verifyDbPrompt?.provider || verifyStageConfig.provider);
  const verifyModel = verifyDbPrompt?.modelId || verifyStageConfig.model || "gpt-5.4";

  // Use Responses API with web_search tool — model searches internet to verify claims
  type VerificationPayload = {
    verifications: Array<{
      claim_id: string;
      status: VerificationStatus;
      source_name?: string;
      source_year?: string;
      source_url?: string;
      source_excerpt?: string;
      corrected_value?: string;
      note: string;
    }>;
  };

  const runVerifyCall = async (prompt: string): Promise<VerificationPayload> => {
    let verifyResponse;
    if (verifyProvider.callResponses) {
      logPublic("ENRICHMENT", `web_search verify: ${claims.length} claims, model=${verifyModel}`);
      verifyResponse = await verifyProvider.callResponses(prompt, {
        model: verifyModel,
        reasoningEffort: "low", // v5: cost cut — fact-check binary z source_excerpt wymogiem nie potrzebuje deep reasoning (MCP forensics: avg 9 931 reasoning tokens/call = $0.40/call thinking budget)
        tools: [{ type: "web_search" }],
        timeoutMs: 900_000, // 15 min — web_search z N claims empirycznie ~885s (BUG #8 cz. 3)
        step: "enrichment_verify", mapId, nodeId,
      });
    } else {
      // Fallback: Chat Completions without web search (Claude or non-reasoning model)
      logPublic("ENRICHMENT", `verify fallback (no web_search): ${claims.length} claims, model=${verifyModel}`);
      verifyResponse = await verifyProvider.call(prompt, {
        model: verifyModel,
        temperature: 0.2,
        step: "enrichment_verify", mapId, nodeId,
      });
    }
    totalCost += verifyResponse.costUsd || 0;
    return parseLLMJSON<VerificationPayload>(verifyResponse.text);
  };

  let verificationData = await runVerifyCall(verifyPrompt);

  // RETRY przy okraglym zerze (2026-08-23): bez wlasnej domeny jako "dowodu"
  // pierwsza proba czesciej konczy sie 0 potwierdzen — jedna dodatkowa proba
  // z szerszymi wskazowkami przywraca zrodla bez obnizania standardu.
  const confirmedFirstPass = verificationData.verifications.filter((v) => v.status !== "unverified").length;
  // retry przy ZERZE, ale tez przy slabym wyniku (np. 1/15 — wlasne widelki
  // cenowe rzadko pokrywaja sie 1:1 z zewnetrznymi stronami)
  const weakResult = confirmedFirstPass === 0 || (claims.length >= 5 && confirmedFirstPass < 2);
  if (weakResult && claims.length > 0) {
    logPublic("ENRICHMENT", `${confirmedFirstPass} potwierdzeń w 1. próbie (${claims.length} twierdzeń) — RETRY weryfikacji z szerszymi zapytaniami...`);
    const retryPrompt = verifyPrompt + `

## RETRY — poprzednia proba nie potwierdzila zadnego twierdzenia
Sprobuj ponownie, inaczej:
- uzyj SZERSZYCH, PROSTSZYCH zapytan (2-4 slowa kluczowe zamiast pelnych zdan)
- dopuszczalne sa takze poradniki branzowe, sklepy i blogi tematyczne, o ile strona faktycznie potwierdza tresc twierdzenia
- jesli liczba w twierdzeniu jest przyblizona, wystarczy zrodlo potwierdzajace rzad wielkosci (status "modified" + corrected_value)
NADAL OBOWIAZUJE: URL musi byc KONKRETNA podstrona istniejacego artykulu znaleziona w wyszukiwaniu, a source_excerpt VERBATIM cytatem z niej. NIE wymyslaj adresow — lepiej "unverified" niz zmyslony URL.`;
    const retryData = await runVerifyCall(retryPrompt);
    const retryConfirmed = retryData.verifications.filter((v) => v.status !== "unverified").length;
    if (retryConfirmed > confirmedFirstPass) {
      verificationData = retryData;
    }
  }

  // Log verification results
  const confirmed = verificationData.verifications.filter(v => v.status === "confirmed").length;
  const modified = verificationData.verifications.filter(v => v.status === "modified").length;
  const unverified = verificationData.verifications.filter(v => v.status === "unverified").length;
  logPublic("ENRICHMENT", `web_search verify: ${confirmed} confirmed, ${modified} corrected, ${unverified} unverified`);
  const sources = verificationData.verifications
    .filter(v => v.source_name && v.status !== "unverified")
    .map(v => `${v.source_name}${v.source_year ? " " + v.source_year : ""}`);
  if (sources.length > 0) {
    logPublic("ENRICHMENT", `sources: [${sources.join(", ")}]`);
  }

  // Step 3: Merge verification results + post-verify URL quality filter.
  // Drops "confirmed"/"modified" claims with poor URLs (root domain, landing pages,
  // very short paths) or empty source_excerpt — re-marks them as "unverified" so
  // they appear in the article without a misleading citation.
  // Domena wlasna projektu nie moze byc zrodlem (decyzja wlasciciela
  // 2026-08-23 po incydencie whiteever.pl cytujacego sam siebie)
  const ownDomain = await getOwnDomain(mapId);

  let urlQualityFiltered = 0;
  const filterReasons: Record<string, number> = {
    root_or_shallow: 0,
    too_short: 0,
    no_excerpt: 0,
    own_domain: 0,
    dead_url: 0,
  };

  const verified: VerifiedClaim[] = claims.map((claim) => {
    const v = verificationData.verifications.find((vv) => vv.claim_id === claim.id);
    let status = (v?.status || "unverified") as VerificationStatus;
    let sourceUrl = v?.source_url ? cleanSourceUrl(v.source_url) : null;
    let sourceName = v?.source_name || null;
    let sourceYear = v?.source_year || null;
    const excerpt = (v?.source_excerpt || "").trim();

    if (status === "confirmed" || status === "modified") {
      const reason: string | null =
        sourceUrl && isSameDomain(sourceUrl, ownDomain)
          ? "own_domain"
          : poorUrlReason(sourceUrl, excerpt);
      if (reason) {
        status = "unverified";
        sourceUrl = null;
        sourceName = null;
        sourceYear = null;
        urlQualityFiltered++;
        filterReasons[reason]++;
      }
    }

    return {
      ...claim,
      status,
      sourceName,
      sourceYear,
      sourceUrl,
      correctedValue: v?.corrected_value || null,
      verificationNote: v?.note || "",
    };
  });

  // KONTROLA ZYCIA URL-i (2026-08-23): weryfikator w ~7% przypadkow wpisywal
  // zmyslony adres mimo realnego wyszukiwania (audyt: 4/60 zrodel = 404).
  // Crawl4AI sprawdza status celu PRZED wstawieniem cytowania; martwe = TYLKO
  // 404/410 (403/timeout = mozliwy bot-block — fail-open, nie wycinamy).
  const urlsToCheck = [...new Set(
    verified
      .filter((c) => (c.status === "confirmed" || c.status === "modified") && c.sourceUrl)
      .map((c) => c.sourceUrl as string),
  )];
  if (urlsToCheck.length > 0) {
    const aliveByUrl = new Map<string, boolean | null>();
    await Promise.all(urlsToCheck.map(async (u) => {
      aliveByUrl.set(u, await checkUrlAlive(`https://${u}`));
    }));
    for (const c of verified) {
      if ((c.status === "confirmed" || c.status === "modified") && c.sourceUrl && aliveByUrl.get(c.sourceUrl) === false) {
        logPublic("ENRICHMENT", `martwy URL źródła (404) — cytowanie wycięte: ${c.sourceUrl}`);
        c.status = "unverified";
        c.sourceUrl = null;
        c.sourceName = null;
        c.sourceYear = null;
        urlQualityFiltered++;
        filterReasons.dead_url = (filterReasons.dead_url || 0) + 1;
      }
    }
  }

  if (urlQualityFiltered > 0) {
    logPublic("ENRICHMENT", `URL quality filter: ${urlQualityFiltered} dropped (root_or_shallow=${filterReasons.root_or_shallow}, too_short=${filterReasons.too_short}, no_excerpt=${filterReasons.no_excerpt}, dead_url=${filterReasons.dead_url})`);
  }

  return { verified, costUsd: totalCost, urlQualityFiltered, filterReasons };
}

// =============================================
// Source injection (POST-humanization, numbered citations + Źródła footer)
// =============================================

export interface InsertSourcesResult {
  html: string;
  injectedCount: number;       // atomic inline insertions (each may contain multiple <sup> tags after URL dedup)
  orphanedCount: number;       // claims that couldn't anchor inline (footer-only)
  footerAppended: boolean;
  uniqueUrlCount: number;      // distinct normalized URLs → distinct footer <li> entries
  totalClaimCount: number;     // total verified claims processed
  supTagCount: number;         // total <sup> tags emitted inline (>= injectedCount when multi-sup occurs)
  adjacentSupCollapsed: number;// duplicate adjacent <sup>[N]</sup><sup>[N]</sup> sequences collapsed by post-process
}

function stripHtmlTags(s: string): string {
  return s.replace(/<[^>]+>/g, "");
}

function normalizeForCompare(s: string): string {
  return stripHtmlTags(s).toLowerCase().replace(/\s+/g, " ").trim();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function ensureHttps(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function splitMultiSource(field: string | null): string[] {
  if (!field) return [];
  // Accept |, ;, and lowercase " and " as separators (LLM is inconsistent).
  // Polish "i" coordinator dropped — too collision-prone with names ("Wnukiewicz i wsp.").
  // "and" case-sensitive — "Standards AND Guidelines" stays whole.
  // Test cases:
  //   "OECD; European Commission"        → ["OECD", "European Commission"]
  //   "Source A | Source B"              → ["Source A", "Source B"]
  //   "WHO and FDA"                      → ["WHO", "FDA"]
  //   "Wnukiewicz i wsp. (2018)"         → ["Wnukiewicz i wsp. (2018)"] (no split, "i" not separator)
  //   "Standards AND Guidelines"         → ["Standards AND Guidelines"] (no split, uppercase AND)
  //   "Single Source"                    → ["Single Source"]
  return field.split(/\s*[|;]\s*|\s+and\s+/).map((s) => s.trim()).filter(Boolean);
}

/**
 * URL normalization for citation dedup. Operates on already-cleaned URLs
 * (cleanSourceUrl stripped scheme + www. + sliced 120 chars upstream).
 *
 *   - Strip query string (?...) and fragment (#...)
 *   - Strip trailing slash(es)
 *   - Lowercase entire string (host + path)
 *
 * Path lowercasing is intentional: case-sensitive paths in the wild are
 * vanishingly rare for cited resources; lowercasing collapses obvious
 * fingerprint duplicates (e.g. /Raport.pdf vs /raport.pdf from different
 * LLM normalizations of the same source).
 */
function normalizeUrlPart(part: string): string {
  return part
    .trim()
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
}

/**
 * Extract anchor tokens — features humanization tends to preserve. Capitalized
 * words use 5-char STEM to handle Polish declension (Cialdiniego → "ciald"
 * matches both genitive "Cialdiniego" and nominative "Cialdini" in the
 * normalized substring scan).
 *
 *  - 4-digit years (1900-2099) — exact
 *  - Numbers with optional decimal (≥2 chars or pure digit) — exact
 *  - Proper-noun stems (5 chars min) — handles inflection
 *  - Quoted phrases ≥8 chars — exact (titles often preserved verbatim)
 */
function extractAnchorTokens(claimText: string): string[] {
  const tokens = new Set<string>();

  const years = claimText.match(/(?:19|20)\d{2}/g) || [];
  for (const y of years) tokens.add(y);

  const numerics = claimText.match(/\d+(?:[.,]\d+)?/g) || [];
  for (const n of numerics) {
    if (n.length >= 2 || /^\d+$/.test(n)) tokens.add(n);
  }

  // Proper-noun stems — slice(0,5) lowercased. Only emit if stem ≥5 chars
  // (avoids 3-letter false positives like "Tym", "Pan" etc.)
  const proper = claimText.match(/\p{Lu}\p{L}{4,}/gu) || [];
  for (const p of proper) {
    const stem = p.toLowerCase().slice(0, 5);
    if (stem.length >= 5) tokens.add(stem);
  }

  const quoted = claimText.match(/[„"][^„"]{4,80}["”]/g) || [];
  for (const q of quoted) {
    const inner = q.replace(/^[„"]|["”]$/g, "").toLowerCase();
    if (inner.length >= 8) tokens.add(inner);
  }

  return Array.from(tokens);
}

/**
 * Cascade match strategy (block-scan, NOT regex-on-raw-html):
 *
 *   Tier 1 — full claim text plaintext substring (handles untouched sentences)
 *   Tier 2 — 60-char prefix plaintext substring (handles tail rewrites)
 *   Tier 3 — anchor token scoring (handles full sentence restructure where
 *            ≥50% of unique tokens like years/numbers/proper-nouns/quotes
 *            land in the same paragraph). Threshold: max(2, ceil(0.5 * tokens.length)).
 *   Tier 4 — orphan (no match; footer-only with -orphan suffix)
 *
 * Block iteration over <p>/<li>/<td>; plaintext via stripHtmlTags + normalize.
 */
function tryInjectInline(html: string, claimText: string, supTag: string): {
  html: string;
  matched: boolean;
} {
  const blockRe = /<(p|li|td)([^>]*)>([\s\S]*?)<\/\1>/gi;

  // --- Tier 1 + Tier 2: substring-based ---
  const substrTries: string[] = [];
  if (claimText.length >= 20) substrTries.push(claimText);
  if (claimText.length > 30) {
    const head = claimText.slice(0, Math.min(60, claimText.length));
    const lastSpace = head.lastIndexOf(" ");
    const prefix = lastSpace > 30 ? head.slice(0, lastSpace) : head;
    if (prefix.length >= 20 && prefix !== claimText) substrTries.push(prefix);
  }

  for (const needle of substrTries) {
    const needleNorm = normalizeForCompare(needle);
    if (needleNorm.length < 20) continue;

    blockRe.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = blockRe.exec(html)) !== null) {
      const blockNorm = normalizeForCompare(m[3]);
      if (blockNorm.includes(needleNorm)) {
        const tag = m[1];
        const closingTag = `</${tag}>`;
        const insertPos = m.index + m[0].length - closingTag.length;
        return {
          html: html.slice(0, insertPos) + supTag + html.slice(insertPos),
          matched: true,
        };
      }
    }
  }

  // --- Tier 3: anchor token scoring ---
  const tokens = extractAnchorTokens(claimText);
  if (tokens.length === 0) return { html, matched: false };

  const required = Math.max(1, Math.ceil(tokens.length * 0.5));
  let bestBlock: { insertPos: number; score: number } | null = null;

  blockRe.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = blockRe.exec(html)) !== null) {
    const blockNorm = normalizeForCompare(m[3]);
    let score = 0;
    for (const tok of tokens) {
      if (blockNorm.includes(tok)) score++;
    }
    if (score >= required && (!bestBlock || score > bestBlock.score)) {
      const tag = m[1];
      const closingTag = `</${tag}>`;
      bestBlock = {
        insertPos: m.index + m[0].length - closingTag.length,
        score,
      };
    }
  }

  if (bestBlock) {
    return {
      html: html.slice(0, bestBlock.insertPos) + supTag + html.slice(bestBlock.insertPos),
      matched: true,
    };
  }

  return { html, matched: false };
}

/**
 * Insert numbered citations (<sup><a href="#zrodlo-N">[N]</a></sup>) inline at claim
 * paragraph end, then append <h2 id="zrodla">Źródła</h2><ol>...</ol> footer.
 *
 * Designed to run POST-humanization to avoid LLM rewrites stripping <sup> tags.
 * Footer is hardcoded PL "Źródła" for v1 (PL-only deployment).
 *
 * Multi-source claims (LLM concatenated `name1 | name2`, `url1 | url2`) get ONE
 * inline number but multiple anchors in the footer <li>, separated by " | ".
 *
 * Orphans (claims whose sentence was rewritten beyond Tier 2 prefix recognition)
 * skip inline injection but still appear in the footer with id="zrodlo-N-orphan".
 */
export function insertSources(
  html: string,
  verifiedClaims: VerifiedClaim[],
  _language: string,  // reserved for future i18n; v1 hardcoded PL "Źródła"
): InsertSourcesResult {
  // Filter: confirmed/modified + non-empty sourceName + non-empty sourceUrl
  const filtered = verifiedClaims.filter(
    (c) =>
      (c.status === "confirmed" || c.status === "modified") &&
      c.sourceName &&
      c.sourceUrl,
  );

  if (filtered.length === 0) {
    return {
      html,
      injectedCount: 0,
      orphanedCount: 0,
      footerAppended: false,
      uniqueUrlCount: 0,
      totalClaimCount: 0,
      supTagCount: 0,
      adjacentSupCollapsed: 0,
    };
  }

  // Sort by paragraph order (ASC) so first-occurrence URL → lowest ID
  const sorted = [...filtered].sort((a, b) => a.position - b.position);

  // Per-URL dedup: each unique normalized URL = one citation ID. Multi-source
  // claims emit multiple <sup> tags inline (one per unique URL after dedup);
  // footer renders one <li> per unique URL in order of first occurrence.
  interface FooterEntry { url: string; year: string | null; n: number }
  const urlToId = new Map<string, number>();
  const orderedFooterEntries: FooterEntry[] = [];
  const claimToIds: number[][] = []; // sorted-index → array of distinct citation IDs
  let nextId = 0;

  for (const claim of sorted) {
    const urls = splitMultiSource(claim.sourceUrl);
    const years = splitMultiSource(claim.sourceYear);
    const seen = new Set<number>();
    const ids: number[] = [];

    for (let u = 0; u < urls.length; u++) {
      const normUrl = normalizeUrlPart(urls[u]);
      if (!normUrl) continue;
      let n: number;
      if (urlToId.has(normUrl)) {
        n = urlToId.get(normUrl)!;
      } else {
        n = ++nextId;
        urlToId.set(normUrl, n);
        orderedFooterEntries.push({
          url: urls[u],
          year: years[u] ?? years[0] ?? null,
          n,
        });
      }
      // Skip duplicate IDs within a single claim (e.g. claim with "X | X")
      if (!seen.has(n)) {
        seen.add(n);
        ids.push(n);
      }
    }

    claimToIds.push(ids);
  }

  // Inject inline FROM LAST claim BACKWARDS so earlier positions stay valid.
  // Multi-ID claim emits N concatenated <sup> tags, inserted atomically.
  // An ID is "matched" if AT LEAST ONE claim sharing it was inlined.
  let result = html;
  let injectedCount = 0;
  let orphanedCount = 0;
  let supTagCount = 0;
  const idsWithInlineMatch = new Set<number>();

  for (let i = sorted.length - 1; i >= 0; i--) {
    const claim = sorted[i];
    const ids = claimToIds[i];
    if (ids.length === 0) {
      orphanedCount++;
      continue;
    }
    const supConcat = ids
      .map((n) => `<sup><a href="#zrodlo-${n}">[${n}]</a></sup>`)
      .join("");
    const inj = tryInjectInline(result, claim.text, supConcat);
    if (inj.matched) {
      result = inj.html;
      injectedCount++;
      supTagCount += ids.length;
      for (const n of ids) idsWithInlineMatch.add(n);
    } else {
      orphanedCount++;
    }
  }

  // Post-process: collapse adjacent duplicate <sup> tags pointing to same citation ID.
  // Edge case: 2+ different claims sharing the same URL get the same citation ID;
  // if both inline'd into the same paragraph block, they appear as
  // <sup>[5]</sup><sup>[5]</sup> right before </p>. Reader sees [5][5] which looks
  // like a typo. Collapse to single <sup>[5]</sup>.
  const DUP_ADJACENT_SUP_RE = /(<sup><a href="#zrodlo-(\d+)">\[\2\]<\/a><\/sup>)(?:\s*<sup><a href="#zrodlo-\2">\[\2\]<\/a><\/sup>)+/g;
  let adjacentSupCollapsed = 0;
  result = result.replace(DUP_ADJACENT_SUP_RE, (_match, firstTag: string) => {
    adjacentSupCollapsed++;
    return firstTag;
  });

  // Build footer — one <li> per unique URL, single anchor (no sub-anchors).
  // Reader sees full URL as anchor text for transparency (no risk of
  // misleading brand attribution).
  const lis = orderedFooterEntries.map(({ url, n }) => {
    const isOrphan = !idsWithInlineMatch.has(n);
    const id = isOrphan ? `zrodlo-${n}-orphan` : `zrodlo-${n}`;
    const fullUrl = ensureHttps(url);
    const anchor = `<a href="${escapeAttr(fullUrl)}" target="_blank" rel="noopener noreferrer nofollow">${escapeHtml(fullUrl)}</a>`;
    return `  <li id="${id}">${anchor}</li>`;
  });
  const footer = `\n\n<h2 id="zrodla">Źródła</h2>\n<ol>\n${lis.join("\n")}\n</ol>`;
  result += footer;

  return {
    html: result,
    injectedCount,
    orphanedCount,
    footerAppended: true,
    uniqueUrlCount: orderedFooterEntries.length,
    totalClaimCount: sorted.length,
    supTagCount,
    adjacentSupCollapsed,
  };
}

// =============================================
// Straznik zmyslonych zrodel (2026-08-23 noc)
//
// INCYDENT: gdy enrichment nie potwierdzil ZADNEGO claima (sources_inserted=0,
// footer_appended=false), model optimization/humanization DOPISAL stopke
// "Zrodla" od siebie — z wymyslonymi URL-ami (oba 404). Modele znaja wzorzec
// naszych artykulow i "uzupelniaja" brakujaca sekcje.
//
// Zasada: w stopce moga byc WYLACZNIE adresy, ktore wstawil insertSources
// (bierzemy je z enrichedHtml — ostatniego zaufanego punktu przed LLM-ami).
// Wszystko inne wycinamy; pusta stopka znika w calosci; osierocone <sup>
// wskazujace na usuniete wpisy tez sa czyszczone.
// =============================================

const FOOTER_BLOCK_RE = /\n*<h2[^>]*(?:id="zrodla"[^>]*>[\s\S]*?|>\s*(?:Źródła|Zrodla|Sources)\s*)<\/h2>\s*<ol>[\s\S]*?<\/ol>/gi;
const FOOTER_LI_RE = /<li[^>]*>[\s\S]*?href="([^"]+)"[\s\S]*?<\/li>/gi;

function normalizeSourceUrl(u: string): string {
  return u.trim().replace(/\/+$/, "").toLowerCase();
}

export interface StripFabricatedResult {
  html: string;
  removedUrls: string[];
  strippedFooter: boolean;
  changed: boolean;
}

/**
 * Usuwa z finalHtml wpisy stopki Zrodla, ktorych NIE wstawil nasz system.
 * referenceHtml = enrichedHtml (stan tuz po insertSources, przed optimization).
 */
export function stripFabricatedSources(finalHtml: string, referenceHtml: string): StripFabricatedResult {
  const allowed = new Set<string>();
  const refFooters = referenceHtml.match(FOOTER_BLOCK_RE) || [];
  for (const block of refFooters) {
    let m: RegExpExecArray | null;
    FOOTER_LI_RE.lastIndex = 0;
    while ((m = FOOTER_LI_RE.exec(block)) !== null) allowed.add(normalizeSourceUrl(m[1]));
  }

  const removedUrls: string[] = [];
  let strippedFooter = false;

  let out = finalHtml.replace(FOOTER_BLOCK_RE, (block) => {
    const keptLis: string[] = [];
    let m: RegExpExecArray | null;
    FOOTER_LI_RE.lastIndex = 0;
    while ((m = FOOTER_LI_RE.exec(block)) !== null) {
      if (allowed.has(normalizeSourceUrl(m[1]))) {
        keptLis.push(m[0]);
      } else {
        removedUrls.push(m[1]);
      }
    }
    if (keptLis.length === 0) {
      strippedFooter = true;
      return ""; // cala stopka zmyslona (lub pusta) -> znika
    }
    const heading = block.match(/<h2[\s\S]*?<\/h2>/i)?.[0] || '<h2 id="zrodla">Źródła</h2>';
    return `\n\n${heading}\n<ol>\n${keptLis.map((li) => `  ${li}`).join("\n")}\n</ol>`;
  });

  // osierocone <sup> po usunietych wpisach (id zrodlo-N juz nie istnieje)
  out = out.replace(/<sup><a href="#(zrodlo-[^"]+)"[^>]*>[\s\S]*?<\/a><\/sup>/gi, (full, id) =>
    out.includes(`id="${id}"`) ? full : "",
  );

  const changed = removedUrls.length > 0 || strippedFooter;
  return { html: changed ? out : finalHtml, removedUrls, strippedFooter, changed };
}
